import { memo, useMemo, useState, useRef, useEffect } from 'react';
import { WorkflowBlock, Connector, BlockDimensions } from '@/types/workflow';
import { Point, PortPosition, getPortPosition, getOutwardVector } from '@/lib/connectorUtils';
import { pointAtArcLength } from '@/lib/connectorPathMath';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';

const DEFAULT_WIDTH = 200;
const DEFAULT_HEIGHT = 80;

const ARROW_LENGTH = 18;
const ARROW_HALF_WIDTH = 7.5;

interface ConnectorPathProps {
  connector: Connector;
  blocks: WorkflowBlock[];
  blockDimensions: Record<string, BlockDimensions>;
  /** True when this connector is the user's current selection. */
  isSelected?: boolean;
  /** Manuell waypoint-drag start (mousedown på en cirkel-handle). Optional
   *  så viewOnly / preview-routes kan utelämna den helt. */
  onWaypointDragStart?: (connectorId: string, waypointIndex: number, currentWaypoints: Point[], e: React.MouseEvent) => void;
  /** Dubbelklick på linjen → lägg till en waypoint vid klick-punkten.
   *  Manuell-only: AI/import-pipen droppar waypoint-payloads. */
  onAddWaypoint?: (connectorId: string, point: Point, insertIndex: number, currentWaypoints: Point[]) => void;
  /** Dubbelklick på en cirkel-handle → ta bort den waypoint:en. */
  onRemoveWaypoint?: (connectorId: string, waypointIndex: number, currentWaypoints: Point[]) => void;
  onUpdateLabel?: (connectorId: string, label: string) => void;
  /** Single-click on the line — emits the connector id and the screen-
   *  space midpoint so the canvas can position a properties popover. */
  onSelect?: (connectorId: string, screenX: number, screenY: number) => void;
  /** C6: emittas vid högerklick på linjen — canvas visar floating context menu. */
  onContextMenuRequest?: (connectorId: string, clientX: number, clientY: number, worldPoint: Point, currentWaypoints: Point[]) => void;
  viewOnly?: boolean;
}

/**
 * Catmull-Rom spline: kurvan passerar exakt genom varje punkt.
 * Waypoints är pass-through-punkter, inte attractors. tension=0 ger
 * standard Catmull-Rom; högre värden tightar kurvan.
 */
function catmullRomPath(pts: Point[], tension = 0.5): string {
  if (pts.length < 2) return '';
  if (pts.length === 2) {
    return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
  }
  // Phantom-punkter i ändarna så tangenter funkar vid endpoints.
  const extended = [
    { x: 2 * pts[0].x - pts[1].x, y: 2 * pts[0].y - pts[1].y },
    ...pts,
    { x: 2 * pts[pts.length - 1].x - pts[pts.length - 2].x, y: 2 * pts[pts.length - 1].y - pts[pts.length - 2].y },
  ];
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < extended.length - 2; i++) {
    const p0 = extended[i - 1];
    const p1 = extended[i];
    const p2 = extended[i + 1];
    const p3 = extended[i + 2];
    const cp1x = p1.x + (p2.x - p0.x) * tension / 3;
    const cp1y = p1.y + (p2.y - p0.y) * tension / 3;
    const cp2x = p2.x - (p3.x - p1.x) * tension / 3;
    const cp2y = p2.y - (p3.y - p1.y) * tension / 3;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

/**
 * Compute arrowhead rotated by the curve's approach direction (free 360° rotation).
 */
function computeArrow(tip: Point, approachDir: { dx: number; dy: number }) {
  const len = Math.hypot(approachDir.dx, approachDir.dy);
  const ndx = len > 0.001 ? approachDir.dx / len : 1;
  const ndy = len > 0.001 ? approachDir.dy / len : 0;

  const angle = Math.atan2(ndy, ndx);
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);

  const base: Point = {
    x: tip.x - cosA * ARROW_LENGTH,
    y: tip.y - sinA * ARROW_LENGTH,
  };
  const left: Point = {
    x: tip.x - cosA * ARROW_LENGTH + sinA * ARROW_HALF_WIDTH,
    y: tip.y - sinA * ARROW_LENGTH - cosA * ARROW_HALF_WIDTH,
  };
  const right: Point = {
    x: tip.x - cosA * ARROW_LENGTH - sinA * ARROW_HALF_WIDTH,
    y: tip.y - sinA * ARROW_LENGTH + cosA * ARROW_HALF_WIDTH,
  };

  const d = `M ${tip.x} ${tip.y} L ${left.x} ${left.y} L ${right.x} ${right.y} Z`;
  return { base, left, right, d };
}

export const ConnectorPath = memo(function ConnectorPath({
  connector, blocks, blockDimensions, isSelected,
  onWaypointDragStart, onAddWaypoint, onRemoveWaypoint,
  onUpdateLabel, onSelect, onContextMenuRequest, viewOnly,
}: ConnectorPathProps) {
  const fromBlock = blocks.find(b => b.id === connector.fromId);
  const toBlock = blocks.find(b => b.id === connector.toId);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editValue, setEditValue] = useState(connector.label || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditingLabel && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingLabel]);

  const commitLabel = () => {
    setIsEditingLabel(false);
    const trimmed = editValue.trim();
    onUpdateLabel?.(connector.id, trimmed);
    if (trimmed) {
      dispatchOnboardingEvent({ name: 'connector:labelAdded', target: connector.id });
    }
  };

  const strokeColor = connector.color || 'hsl(var(--primary))';

  const pathInfo = useMemo(() => {
    if (!fromBlock || !toBlock) return null;

    const fromDims = blockDimensions[fromBlock.id] || { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    const toDims = blockDimensions[toBlock.id] || { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };

    const fromPort: PortPosition = getPortPosition(
      fromBlock, fromDims.width, fromDims.height,
      connector.fromPort || 'right'
    );
    const toPort: PortPosition = getPortPosition(
      toBlock, toDims.width, toDims.height,
      connector.toPort || 'left'
    );

    const fromOut = getOutwardVector(fromPort.side);
    const toOut = getOutwardVector(toPort.side);

    // Manuella waypoints är ENDA tillåtna källan. AI-emit:ade /
    // cloud-sparade waypoints stripp:as i compactWorkflow + autoFixImport
    // + workflowDetailsSplit innan de når hit. Allt som finns i
    // `connector.waypoints` här kommer från dubbelklick på linjen.
    const waypoints: Point[] = connector.waypoints ?? [];

    let d: string;

    if (waypoints.length === 0) {
      // Smart bezier — approach direction derived from bezier tangent.
      const dx = toPort.x - fromPort.x;
      const dy = toPort.y - fromPort.y;
      const dist = Math.hypot(dx, dy);
      const armLen = Math.min(250, Math.max(50, dist * 0.4));

      const cp1: Point = {
        x: fromPort.x + fromOut.dx * armLen,
        y: fromPort.y + fromOut.dy * armLen,
      };
      const approachDir = { dx: -toOut.dx, dy: -toOut.dy };
      const arrow = computeArrow(toPort, approachDir);

      const cp2: Point = {
        x: arrow.base.x + toOut.dx * armLen,
        y: arrow.base.y + toOut.dy * armLen,
      };

      d = `M ${fromPort.x} ${fromPort.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${arrow.base.x} ${arrow.base.y}`;

      const sourceArrow = computeArrow(fromPort, { dx: -fromOut.dx, dy: -fromOut.dy });

      const allPts = [fromPort, cp1, cp2, arrow.base, toPort, arrow.left, arrow.right, sourceArrow.left, sourceArrow.right];
      const pad = 20;
      const minX = Math.min(...allPts.map(p => p.x)) - pad;
      const minY = Math.min(...allPts.map(p => p.y)) - pad;
      const maxX = Math.max(...allPts.map(p => p.x)) + pad;
      const maxY = Math.max(...allPts.map(p => p.y)) + pad;

      const chordMidX = (fromPort.x + arrow.base.x) / 2;
      const chordMidY = (fromPort.y + arrow.base.y) / 2;
      const curveMidX = (fromPort.x + 3 * cp1.x + 3 * cp2.x + arrow.base.x) / 8;
      const curveMidY = (fromPort.y + 3 * cp1.y + 3 * cp2.y + arrow.base.y) / 8;
      const bulgeX = curveMidX - chordMidX;
      const bulgeY = curveMidY - chordMidY;
      const bulgeMag = Math.hypot(bulgeX, bulgeY);
      const LABEL_OFFSET = 12;
      const labelPoint: Point = bulgeMag > 0.5
        ? {
            x: curveMidX + (bulgeX / bulgeMag) * LABEL_OFFSET,
            y: curveMidY + (bulgeY / bulgeMag) * LABEL_OFFSET,
          }
        : { x: curveMidX, y: curveMidY - LABEL_OFFSET };

      return {
        d, arrowD: arrow.d, sourceArrowD: sourceArrow.d,
        waypoints, left: minX, top: minY, width: maxX - minX, height: maxY - minY,
        fromPort, arrowBase: arrow.base, labelPoint,
      };
    }

    // With waypoints: arrow rotates based on last waypoint → target direction.
    const lastWp = waypoints[waypoints.length - 1];
    const approachDir = { dx: toPort.x - lastWp.x, dy: toPort.y - lastWp.y };
    const arrow = computeArrow(toPort, approachDir);

    const firstWp = waypoints[0];
    const sourceApproach = { dx: fromPort.x - firstWp.x, dy: fromPort.y - firstWp.y };
    const sourceArrow = computeArrow(fromPort, sourceApproach);

    const throughPoints = [fromPort, ...waypoints, arrow.base];
    d = catmullRomPath(throughPoints);

    const allPts = [...throughPoints, toPort, arrow.left, arrow.right, sourceArrow.left, sourceArrow.right];
    const pad = 20;
    const minX = Math.min(...allPts.map(p => p.x)) - pad;
    const minY = Math.min(...allPts.map(p => p.y)) - pad;
    const maxX = Math.max(...allPts.map(p => p.x)) + pad;
    const maxY = Math.max(...allPts.map(p => p.y)) + pad;

    const geoMid = pointAtArcLength(throughPoints, 0.5);
    const labelPoint: Point = { x: geoMid.x, y: geoMid.y - 12 };

    return {
      d, arrowD: arrow.d, sourceArrowD: sourceArrow.d,
      waypoints, left: minX, top: minY, width: maxX - minX, height: maxY - minY,
      fromPort, arrowBase: arrow.base, labelPoint,
    };
  }, [fromBlock, toBlock, blockDimensions, connector.fromPort, connector.toPort, connector.waypoints]);

  /** Convert clientX/Y to SVG world coordinates. */
  const clientToWorld = (e: React.MouseEvent): Point | null => {
    const svg = (e.target as SVGElement).ownerSVGElement ?? (e.target as SVGSVGElement);
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const svgPt = pt.matrixTransform(ctm.inverse());
    return { x: svgPt.x, y: svgPt.y };
  };

  /** Dubbelklick på linjen lägger till en ny waypoint vid klick-punkten. */
  const handleDoubleClick = (e: React.MouseEvent<SVGPathElement>) => {
    if (viewOnly || !pathInfo || !onAddWaypoint) return;
    e.stopPropagation();
    e.preventDefault();
    const clickPoint = clientToWorld(e);
    if (!clickPoint) return;

    // Hitta bästa insertions-index: närmaste punkt på varje segment till klicket.
    const throughPts: Point[] = [pathInfo.fromPort, ...pathInfo.waypoints, pathInfo.arrowBase];
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < throughPts.length - 1; i++) {
      const a = throughPts[i];
      const b = throughPts[i + 1];
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const len2 = abx * abx + aby * aby;
      const t = len2 > 0 ? Math.max(0, Math.min(1, ((clickPoint.x - a.x) * abx + (clickPoint.y - a.y) * aby) / len2)) : 0;
      const projX = a.x + t * abx;
      const projY = a.y + t * aby;
      const dist = Math.hypot(clickPoint.x - projX, clickPoint.y - projY);
      if (dist < bestDist) { bestDist = dist; bestIdx = i; }
    }

    onAddWaypoint(connector.id, clickPoint, bestIdx, [...pathInfo.waypoints]);
    dispatchOnboardingEvent({ name: 'connector:waypointAdded', target: connector.id });
  };

  /** Dubbelklick på en waypoint-handle tar bort den. */
  const handleWaypointDoubleClick = (e: React.MouseEvent, index: number) => {
    if (viewOnly || !pathInfo || !onRemoveWaypoint) return;
    e.stopPropagation();
    e.preventDefault();
    onRemoveWaypoint(connector.id, index, [...pathInfo.waypoints]);
  };

  if (!pathInfo) return null;

  return (
    <svg
      role="img"
      aria-label={`Connection from ${fromBlock?.config.name || 'unknown'} to ${toBlock?.config.name || 'unknown'}`}
      className="absolute pointer-events-none"
      style={{
        left: pathInfo.left,
        top: pathInfo.top,
        width: pathInfo.width,
        height: pathInfo.height,
        overflow: 'visible',
      }}
      viewBox={`${pathInfo.left} ${pathInfo.top} ${pathInfo.width} ${pathInfo.height}`}
    >
      {!viewOnly && (
        <path
          d={pathInfo.d}
          data-guide="connector-path"
          stroke="transparent"
          strokeWidth={16}
          fill="none"
          strokeLinecap="round"
          style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
          // onPointerUp i stället för onClick — på mobil suppress:as
          // syntetisk click när canvas-inner har touch-action:none och
          // touch-events preventDefault:as. PointerUp fyrar oavsett.
          // onPointerDown stoppar propagation så canvas-click-handlern
          // inte rensar selection direkt.
          onPointerDown={(e) => { e.stopPropagation(); }}
          onPointerUp={(e) => {
            if (!onSelect) return;
            e.stopPropagation();
            onSelect(connector.id, e.clientX, e.clientY);
          }}
          onDoubleClick={handleDoubleClick}
          onContextMenu={(e) => {
            if (!onContextMenuRequest) return;
            e.preventDefault();
            e.stopPropagation();
            const world = clientToWorld(e);
            if (!world) return;
            onContextMenuRequest(connector.id, e.clientX, e.clientY, world, [...pathInfo.waypoints]);
          }}
        />
      )}
      {isSelected && (
        <path d={pathInfo.d} stroke={strokeColor} strokeOpacity={0.25} strokeWidth={10} fill="none" strokeLinecap="round" />
      )}
      <path
        d={pathInfo.d}
        stroke={connector.pulse && connector.pulseColor ? connector.pulseColor : strokeColor}
        strokeWidth={isSelected ? 3 : 2}
        fill="none"
        strokeLinecap="round"
        className={connector.pulse ? 'connector-pulse' : undefined}
      />
      <path d={pathInfo.arrowD} fill={strokeColor} stroke="none" />
      {connector.bidirectional && (
        <path d={pathInfo.sourceArrowD} fill={strokeColor} stroke="none" />
      )}
      {/* Waypoint-handles — small draggable circles. Dubbelklick tar bort. */}
      {!viewOnly && pathInfo.waypoints.map((wp, i) => (
        <circle
          key={i}
          cx={wp.x}
          cy={wp.y}
          r={5}
          fill="hsl(var(--primary) / 0.15)"
          stroke="hsl(var(--primary) / 0.5)"
          strokeWidth={1.5}
          style={{ cursor: 'grab', pointerEvents: 'auto' }}
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            el.setAttribute('fill', 'hsl(var(--primary) / 0.4)');
            el.setAttribute('stroke', 'hsl(var(--primary))');
            el.setAttribute('r', '6');
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget;
            el.setAttribute('fill', 'hsl(var(--primary) / 0.15)');
            el.setAttribute('stroke', 'hsl(var(--primary) / 0.5)');
            el.setAttribute('r', '5');
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onWaypointDragStart?.(connector.id, i, [...pathInfo.waypoints], e);
          }}
          onDoubleClick={(e) => handleWaypointDoubleClick(e, i)}
        />
      ))}
      {/* Connector label */}
      {(connector.label || (!viewOnly && isEditingLabel)) && pathInfo.labelPoint && (
        isEditingLabel ? (
          <foreignObject
            data-guide="connector-midpoint"
            x={pathInfo.labelPoint.x - 70}
            y={pathInfo.labelPoint.y - 12}
            width={140}
            height={26}
            style={{ pointerEvents: 'auto', overflow: 'visible' }}
          >
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitLabel();
                if (e.key === 'Escape') { setIsEditingLabel(false); setEditValue(connector.label || ''); }
              }}
              placeholder="Label…"
              style={{
                width: '100%',
                fontSize: '11px',
                textAlign: 'center',
                border: '1px solid hsl(var(--primary))',
                borderRadius: '4px',
                padding: '2px 4px',
                background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))',
                outline: 'none',
              }}
            />
          </foreignObject>
        ) : (
          <g
            data-guide="connector-midpoint"
            style={{ pointerEvents: viewOnly ? 'none' : 'auto', cursor: viewOnly ? 'default' : 'pointer' }}
            onClick={(e) => {
              if (viewOnly) return;
              e.stopPropagation();
              setEditValue(connector.label || '');
              setIsEditingLabel(true);
            }}
          >
            <rect
              x={pathInfo.labelPoint.x - 28}
              y={pathInfo.labelPoint.y - 10}
              width={56}
              height={18}
              rx={4}
              fill="hsl(var(--card))"
              stroke="hsl(var(--primary) / 0.4)"
              strokeWidth={1}
            />
            <text
              x={pathInfo.labelPoint.x}
              y={pathInfo.labelPoint.y + 3}
              textAnchor="middle"
              fontSize="11"
              fill="hsl(var(--foreground))"
              style={{ userSelect: 'none' }}
            >
              {connector.label}
            </text>
          </g>
        )
      )}
      {/* Discoverable hit-zone to add a label when none exists. */}
      {!viewOnly && !connector.label && !isEditingLabel && pathInfo.labelPoint && (
        <g
          data-guide="connector-midpoint"
          className="connector-label-affordance"
          style={{ pointerEvents: 'auto', cursor: 'pointer', opacity: 0.55, transition: 'opacity 0.15s ease' }}
          onMouseEnter={(e) => { (e.currentTarget as SVGGElement).style.opacity = '1'; }}
          onMouseLeave={(e) => { (e.currentTarget as SVGGElement).style.opacity = '0.55'; }}
          onClick={(e) => {
            e.stopPropagation();
            setEditValue('');
            setIsEditingLabel(true);
          }}
        >
          <circle
            cx={pathInfo.labelPoint.x}
            cy={pathInfo.labelPoint.y + 4}
            r={22}
            fill="transparent"
          />
          <rect
            x={pathInfo.labelPoint.x - 30}
            y={pathInfo.labelPoint.y - 10}
            width={60}
            height={18}
            rx={9}
            fill="hsl(var(--primary) / 0.12)"
            stroke="hsl(var(--primary))"
            strokeDasharray="3 3"
            strokeWidth={1}
          />
          <text
            x={pathInfo.labelPoint.x}
            y={pathInfo.labelPoint.y + 3}
            textAnchor="middle"
            fontSize="10"
            fontWeight={600}
            fill="hsl(var(--primary))"
            style={{ userSelect: 'none' }}
          >
            + Label
          </text>
          <title>Click to add label</title>
        </g>
      )}
    </svg>
  );
});
